import React, { useState, useContext, useEffect, useRef } from "react";
import { generateResume, getUserResumes } from "../api/api";
import { AuthContext } from "../AuthContext";
import { jwtDecode } from "jwt-decode";
import { Toaster, toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import emailjs from "@emailjs/browser";

function Dashboard() {
  const [jobDescription, setJobDescription] = useState("");
  const [feedback, setFeedback] = useState("");
  const { session, supabase } = useContext(AuthContext);
  const navigate = useNavigate();
  const [hasResume, setHasResume] = useState(false);
  const [isCheckingResumes, setIsCheckingResumes] = useState(false);
  const [isGeneratingResume, setIsGeneratingResume] = useState(false);
  const form = useRef();

  useEffect(() => {
    const checkSubscription = async (userId) => {
      console.log("Checking subscription for user:", userId);
      try {
        const { data, error } = await supabase.rpc("has_active_subscription"); // No parameters needed now

        console.log("Subscription check response:", { data, error });

        if (error) throw error;
        return data[0]?.is_subscribed || false;
      } catch (error) {
        console.error("Error checking subscription:", error);
        return false;
      }
    };

    const checkUserResume = async () => {
      setIsCheckingResumes(true);
      try {
        const jwtToken = session.access_token;
        const decodedToken = jwtDecode(jwtToken);
        const userId = decodedToken.user_id;

        const resumesData = await getUserResumes(jwtToken);
        console.log("Number of resumes:", resumesData.length);

        if (resumesData.length >= 2) {
          // Only check subscription if user has hit the limit
          const isSubscribed = await checkSubscription(userId);
          console.log("User is subscribed:", isSubscribed);
          setHasResume(!isSubscribed); // If subscribed, they can continue (hasResume = false)
        } else {
          setHasResume(false); // Under 5 resumes, they can continue
        }
      } catch (error) {
        console.error("Error checking user resumes:", error);
        toast.error("Failed to check for existing resumes.");
      } finally {
        setIsCheckingResumes(false);
      }
    };

    if (session && !hasResume) {
      checkUserResume();
    }
  }, [session, hasResume, supabase]);

  const handleGenerateResume = async () => {
    if (hasResume) {
      toast.error("You can only generate one resume.");
      return;
    }

    setIsGeneratingResume(true);

    try {
      const jwtToken = session.access_token;
      const response = await generateResume(jobDescription, jwtToken);
      setJobDescription("");
      toast.success(response.message);
      navigate("/resume-editor", {
        state: { resume: response.modified_experiences },
      });
    } catch (error) {
      console.log(error);
      if (error.response && error.response.status === 404) {
        toast.error("Please update your Profile information first.");
      } else {
        toast.error("Failed to generate Resume. Please contact us for help.");
      }
    } finally {
      setIsGeneratingResume(false);
    }
  };

  const sendEmail = (e) => {
    e.preventDefault();

    const templateParams = {
      message: feedback,
      user_email: session.user.email,
      user_name: session.user.user_metadata.full_name || "Anonymous",
    };

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then((response) => {
        console.log(
          "Feedback sent successfully:",
          response.status,
          response.text
        );
        console.log(session.user);
        toast.success("Thank you for your feedback!");
        setFeedback("");
      })
      .catch((error) => {
        console.error("Error sending feedback:", error);
        toast.error("Failed to send feedback. Please try again later.");
      });
  };

  const isTextBoxEmpty = () => {
    return jobDescription.trim() === "";
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 -mt-20">
      <div className="max-w-8xl mx-auto">
        {isCheckingResumes ? (
          <div className="flex items-center justify-center">
            <FaSpinner className="animate-spin text-purple-500" size={24} />
            <span className="ml-2">Checking for existing resumes...</span>
          </div>
        ) : isGeneratingResume ? (
          <div className="flex items-center justify-center">
            <FaSpinner className="animate-spin text-purple-500" size={24} />
            <span className="ml-2">Generating your resume...</span>
          </div>
        ) : hasResume ? (
          <div>
            <h2 className="text-xl font-bold leading-tight text-gray-900 sm:text-4xl sm:tracking-tight">
              We'd Love to Hear Your Thoughts!
            </h2>
            <form ref={form} onSubmit={sendEmail} className="mt-8">
              {/* <label
                htmlFor="user_name"
                className="block mb-2 text-lg font-medium text-gray-900"
              >
                Name
              </label>
              <input
                type="text"
                name="user_name"
                className="block w-full p-2 border border-gray-300 rounded"
                required
              /> */}

              <textarea
                name="message"
                className="block mt-10 w-full h-48 p-3 border border-purple-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows="4"
                placeholder="Type your feedback here..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                required
              />

              <button
                type="submit"
                className="bg-purple-400 hover:bg-purple-600 text-white font-bold mt-3 py-2 px-4 rounded-3xl mt-6"
              >
                Submit Feedback
              </button>
            </form>
          </div>
        ) : (
          <div>
            <h2 className="text-3xl font-bold leading-tight text-gray-900 sm:text-4xl sm:tracking-tight">
              Generate a Resume for Every Job Application
            </h2>
            <div className="mt-8">
              <label
                htmlFor="job-description"
                className="block mb-2 text-lg font-medium text-gray-900"
              >
                Paste the job description below to craft your tailored resume
              </label>
              <textarea
                id="job-description"
                placeholder="Job Description"
                rows="10"
                value={jobDescription}
                className="block w-full max-w-2xl mx-auto p-4 text-gray-900 border border-gray-300 rounded-lg bg-gray-50 text-base focus:ring-purple-300 focus:border-purple-300"
                onChange={(e) => setJobDescription(e.target.value)}
              />
            </div>
            <button
              onClick={handleGenerateResume}
              disabled={isTextBoxEmpty()}
              className="bg-purple-400 hover:bg-purple-600 text-white font-bold mt-3 py-2 px-4 rounded-3xl mt-6"
            >
              Generate
            </button>
          </div>
        )}
      </div>
      <Toaster
        position="top-center"
        reverseOrder={false}
        gutter={8}
        containerClassName=""
        containerStyle={{}}
        toastOptions={{
          className: "",
          duration: 5000,
          style: {
            background: "#fff",
            color: "#BF40BF",
            borderRadius: "8px",
          },
          success: {
            duration: 3000,
            theme: {
              primary: "green",
              secondary: "black",
            },
          },
        }}
      />
    </div>
  );
}

export default Dashboard;
